'use client';
import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    Select,
    MenuItem,
    TextField,
    Button,
    SelectChangeEvent,
} from '@mui/material';
import { CheckedRadioButton, UnCheckedRadioButton, CloseIcon } from '@/public/icon';
import { IGroup } from '@/app/models/group/IGroup';
import ImagePicker from '../../MUI/ImagePicker';
import { MediaOperation } from '@/app/models/media/IMedia';
import JoyPlayer from '@/app/base-ui/ReactPlayerVideo';
import moment from 'moment';
import { AddImage } from '@/public/icon';
import Image from 'next/image';
import { IconButton } from '@mui/material';
import MUIBoxScrollBehavior from '../../MUI/BoxScrollBehavior';
import { useTheme } from '@mui/material';
import { fileHelper } from '@/app/helpers/handleFileBinary';
import ButtonHandleUrl from '../Writing/ButtonHandleUrl';
import CaptionEditor from './CaptionEditor';
import { cleanYouTubeUrl } from '../UrlFormWriting/components/YouTubeThumbnail';
import { useSnackbar } from '@/app/contexts/snack-bar-provider';
import { useTranslation } from '@/app/hooks/useTranslation';

interface NewWritingFormProps {
    groups: IGroup[];
    initialData?: {
        caption?: string;
        media?: MediaOperation[];
        groupId?: string;
        url?: string | null;
        mediaType?: 'image' | 'URL';
        tags?: string[];
        boardType?: 'market' | 'board';
        price?: string;
        productName?: string;
    };
    onSubmit: (data: {
        boardType: 'market' | 'board';
        mediaType: 'image' | 'URL';
        groupId: string;
        media: MediaOperation[];
        caption: string;
        tags: string[];
        price?: string;
        productName?: string;
    }) => void;
    onSwitchToUrl: () => void;
    onBackToUrlScreen?: () => void;
    handleAvatarChange: (selectedImages: MediaOperation[]) => Promise<void>;
    removeImage: (image: MediaOperation) => Promise<void>;
    onCaptionChange?: (caption: string) => void;
    onDataChange?: (data: { groupId: string; tags: string[]; caption: string; boardType: 'market' | 'board'; price: string; productName: string }) => void;
    onClearUrl?: () => void;
    handleDeleteUrl?: (item: MediaOperation) => void;
    onMediaTypeChange?: (mediaType: 'image' | 'URL') => void;
    isEditMode?: boolean;
}

export interface NewWritingFormRef {
    handleSubmit: () => void;
    canSubmit: () => boolean;
}

const imageWidth = 80;
const imageHeight = 80;
const imageRadius = 10;

const NewWritingForm = forwardRef<NewWritingFormRef, NewWritingFormProps>(
    ({ groups, initialData, onSubmit, onSwitchToUrl, onBackToUrlScreen, handleAvatarChange, removeImage, onCaptionChange, onDataChange, onClearUrl, handleDeleteUrl, onMediaTypeChange, isEditMode }, ref) => {
        const theme = useTheme();
        const { addSnackbar } = useSnackbar();
        const { t } = useTranslation();
        const isInitialMount = useRef(true);
        const [boardType, setBoardType] = useState<'market' | 'board'>(initialData?.boardType || 'board');
        const [mediaType, setMediaType] = useState<'image' | 'URL'>(initialData?.mediaType || 'image');
        const [selectedGroup, setSelectedGroup] = useState<string>(initialData?.groupId || 'all');
        const [caption, setCaption] = useState(initialData?.caption || '');
        const [tagInput, setTagInput] = useState('');
        const [tags, setTags] = useState<string[]>(initialData?.tags || []);
        const [price, setPrice] = useState(initialData?.price || '');
        const [productName, setProductName] = useState(initialData?.productName || '');
        
        const MAX_TAGS = 5;

        // Format price với thousand separator
        const formatPrice = (value: string) => {
            // Remove all non-digit characters
            const numericValue = value.replace(/\D/g, '');
            // Format with thousand separator
            return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        };

        const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;
            // Remove all non-digit characters for storage
            const numericValue = inputValue.replace(/\D/g, '');
            setPrice(numericValue);
            onDataChange?.({ groupId: selectedGroup, tags, caption, boardType, price: numericValue, productName });
        };

        // Only sync with initialData on initial mount, not on subsequent updates
        // This prevents re-renders after save when parent updates initialData
        useEffect(() => {
            if (initialData && isInitialMount.current) {
                if (initialData.caption !== undefined) {
                    setCaption(initialData.caption);
                }
                if (initialData.mediaType !== undefined) {
                    setMediaType(initialData.mediaType);
                }
                if (initialData.groupId !== undefined) {
                    setSelectedGroup(initialData.groupId);
                }
                if (initialData.tags !== undefined) {
                    setTags(initialData.tags);
                }
                if (initialData.boardType !== undefined) {
                    setBoardType(initialData.boardType);
                }
                if (initialData.price !== undefined) {
                    setPrice(initialData.price);
                }
                if (initialData.productName !== undefined) {
                    setProductName(initialData.productName);
                }
                isInitialMount.current = false;
            }
        }, [initialData]);

        useImperativeHandle(ref, () => ({
            handleSubmit: () => {
                onSubmit({
                    boardType,
                    mediaType,
                    groupId: selectedGroup,
                    media: initialData?.media || [],
                    caption,
                    tags,
                    price: boardType === 'market' ? price : undefined,
                    productName: boardType === 'market' ? productName : undefined,
                });
            },
            canSubmit: () => {
                // Must have media (image/video OR URL)
                const hasMedia = (initialData?.media && initialData.media.length > 0) || (mediaType === 'URL' && !!initialData?.url);

                // Must have caption
                const hasCaption = !!caption.trim();

                // If market (product), must also have productName and price
                if (boardType === 'market') {
                    return hasMedia || hasCaption && !!productName.trim() && !!price.trim();
                }

                // If board (post), must have media and caption
                return hasMedia || hasCaption;
            },
        }));

        const handleBoardTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const newBoardType = event.target.value as 'market' | 'board';
            setBoardType(newBoardType);
            onDataChange?.({ groupId: selectedGroup, tags, caption, boardType: newBoardType, price, productName });
        };

        const handleMediaTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const newMediaType = event.target.value as 'image' | 'URL';
            setMediaType(newMediaType);

            // Notify parent component about media type change
            onMediaTypeChange?.(newMediaType);

            // When switching to URL, navigate to URL form
            if (newMediaType === 'URL') {
                onDataChange?.({ groupId: selectedGroup, tags, caption, boardType, price, productName });
                onSwitchToUrl();
            }

            // When switching to image, just update the mediaType state
            // Don't clear URL data - it will be cleared only when user explicitly deletes it or when saving
        };

        const handleGroupChange = (event: SelectChangeEvent) => {
            const newGroup = event.target.value;
            setSelectedGroup(newGroup);
            onDataChange?.({ groupId: newGroup, tags, caption, boardType, price, productName });
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (tags.length >= MAX_TAGS) {
                    addSnackbar({
                        message: t('max_tags_error', `태그는 최대 ${MAX_TAGS}개까지 입력할 수 있습니다.`).replace('{count}', MAX_TAGS.toString()),
                        severity: 'error',
                        autoHideDuration: 3000,
                        horizontal: 'center',
                        vertical: 'bottom',
                        type: 'toast',
                    });
                    return;
                }
                if (tagInput.trim() && !tags.includes(tagInput.trim())) {
                    const newTags = [...tags, tagInput.trim()];
                    setTags(newTags);
                    onDataChange?.({ groupId: selectedGroup, tags: newTags, caption, boardType, price, productName });
                }
                setTagInput('');
            }
        };

        const handleAddTag = () => {
            if (tags.length >= MAX_TAGS) {
                addSnackbar({
                    message: t('max_tags_error', `태그는 최대 ${MAX_TAGS}개까지 입력할 수 있습니다.`).replace('{count}', MAX_TAGS.toString()),
                    severity: 'error',
                    autoHideDuration: 3000,
                    horizontal: 'center',
                    vertical: 'bottom',
                    type: 'toast',
                });
                return;
            }
            if (tagInput.trim() && !tags.includes(tagInput.trim())) {
                const newTags = [...tags, tagInput.trim()];
                setTags(newTags);
                onDataChange?.({ groupId: selectedGroup, tags: newTags, caption, boardType, price, productName });
            }
            setTagInput('');
        };

        const filteredPhotos = initialData?.media?.filter((item: any) => {
            if ('isDeleted' in item && item.isDeleted) return false;
            if (mediaType === 'image') {
                // Only show image/video that are NOT url type
                // Check if it's NOT a url media (url media has 'url' property or mediaType === 'url')
                const isUrlMedia = item.mediaType === 'url' || ('url' in item && item.url);
                if (isUrlMedia) return false;

                return item.mediaType === 'image' || item.mediaType === 'video';
            }
            return false;
        }) || [];

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    backgroundColor: 'background.paper',
                    gap: 1
                }}
            >
                <Box
                    sx={{
                        px: 5,
                        backgroundColor: 'white',
                    }}
                >
                    {/* 게시판 선택 */}
                    <Box sx={{
                            py: 4,
                            borderBottom: '1px solid',
                            borderColor: 'grayscale.gray02'
                        }}
                    >
                        <Typography
                            variant="h2_14Medium"
                            component="div"
                            sx={{ mb: 3, color: 'grayscale.gray07' }}
                        >
                            {t('board_selection', '게시판 선택')}
                        </Typography>
                        <RadioGroup
                            row
                            value={boardType}
                            onChange={handleBoardTypeChange}
                            sx={{ gap: 2, display: 'flex', flexWrap: 'nowrap' }}
                        >
                            <FormControlLabel
                                value="market"
                                disabled={isEditMode}
                                control={
                                    <Radio
                                        icon={<UnCheckedRadioButton />}
                                        checkedIcon={<CheckedRadioButton />}
                                        sx={{ padding: 0, mr: 2.5 }}
                                        disabled={isEditMode}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography
                                            variant="h2_14Medium"
                                            sx={{ color: isEditMode ? 'grayscale.gray04' : 'grayscale.gray07', whiteSpace: 'nowrap' }}
                                        >
                                            {t('joker_market', '조커 마켓')}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                color: '#BBBBBB',
                                                lineHeight: 1.4,
                                                wordBreak: 'keep-all'
                                            }}
                                        >
                                            {t('joker_market_desc', '내 작품이나 굿즈, 용품, 책을 거래해요')}
                                        </Typography>
                                    </Box>
                                }
                                sx={{ml:0, alignItems: 'flex-start', flex: 1.3, minWidth: 0}}
                            />
                            <FormControlLabel
                                value="board"
                                disabled={isEditMode}
                                control={
                                    <Radio
                                        icon={<UnCheckedRadioButton />}
                                        checkedIcon={<CheckedRadioButton />}
                                        sx={{ padding: 0, mr: 2.5 }}
                                        disabled={isEditMode}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Typography
                                            variant="h2_14Medium"
                                            sx={{ color: isEditMode ? 'grayscale.gray04' : 'grayscale.gray07', whiteSpace: 'nowrap' }}
                                        >
                                            {t('board', '게시판')}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                color: '#BBBBBB',
                                                lineHeight: 1.4,
                                                wordBreak: 'keep-all'
                                            }}
                                        >
                                            {t('board_desc', '내 작품을 자랑해요')}
                                        </Typography>
                                    </Box>
                                }
                                sx={{ml:0, alignItems: 'flex-start', flex: 1, minWidth: 0}}
                            />
                        </RadioGroup>
                    </Box>

                    {/* 유형 */}
                    <Box
                        sx={{
                            py: 4,
                            borderBottom: '1px solid',
                            borderColor: 'grayscale.gray02'
                        }}
                    >
                        <Typography
                            variant="h2_14Medium"
                            component="div"
                            sx={{ mb: 3, color: 'grayscale.gray07' }}
                        >
                            {t('type', '유형')}
                        </Typography>
                        <RadioGroup
                            row
                            value={mediaType}
                            onChange={handleMediaTypeChange}
                            sx={{ gap: 10 }}
                        >
                            <FormControlLabel
                                value="image"
                                control={
                                    <Radio
                                        icon={<UnCheckedRadioButton />}
                                        checkedIcon={<CheckedRadioButton />}
                                        sx={{ padding: 0, mr: 2.5 }}
                                    />
                                }
                                label={
                                    <Typography
                                        variant="h2_14Medium"
                                        sx={{ color: 'grayscale.gray07' }}
                                    >
                                        {t('image_type', '이미지')}
                                    </Typography>
                                }
                                sx={{ml:0}}
                            />
                            <FormControlLabel
                                value="URL"
                                control={
                                    <Radio
                                        icon={<UnCheckedRadioButton />}
                                        checkedIcon={<CheckedRadioButton />}
                                        sx={{ padding: 0, mr: 2.5 }}
                                    />
                                }
                                label={
                                    <Typography
                                        variant="h2_14Medium"
                                        sx={{ color: 'grayscale.gray07' }}
                                    >
                                        {t('video_url_type', '동영상 URL')}
                                    </Typography>
                                }
                                sx={{ml:0}}
                            />
                        </RadioGroup>
                    </Box>
                    <Box sx={{py:4}}>
                        <Typography
                            variant="h2_14Medium"
                            component="div"
                            sx={{ mb: 3, color: 'grayscale.gray07' }}
                        >
                            {t('group', '그룹')}
                        </Typography>
                        <Select
                            value={selectedGroup}
                            onChange={handleGroupChange}
                            displayEmpty
                            fullWidth
                            sx={{
                                '& .MuiSelect-select': {
                                    py: 3.5,
                                    px: 4,
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'grayscale.gray02',
                                    borderRadius: '16px',
                                },
                            }}
                        >
                            <MenuItem
                                value="all"
                                sx={{
                                    '&.Mui-selected': {
                                        color: theme.palette.primary.main,
                                        backgroundColor: '#fff !important',
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: '#fff',
                                    },
                                }}
                            >
                                {t('default_group', '기본')}
                            </MenuItem>
                            {groups?.map((group) => (
                                <MenuItem
                                    key={group.id}
                                    value={group.id.toString()}
                                    sx={{
                                        '&.Mui-selected': {
                                            color: theme.palette.primary.main, 
                                            backgroundColor: '#fff',
                                        },
                                        '&.Mui-selected:hover': {
                                            backgroundColor: '#fff',
                                        },
                                    }}
                                >
                                    {group.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>

                <Box
                    sx={{
                        px: 5,
                        pt: 5,
                        pb: 4,
                        backgroundColor: 'white'
                    }}
                >
                    {mediaType === 'image' ? (
                        <>
                            <MUIBoxScrollBehavior
                            enableDragScroll={true}
                            showScrollWhenHover={true}
                            sx={{
                                '&::-webkit-scrollbar': { width: '.2rem', height: '.2rem' },
                                background: theme.palette.background.default,
                                pt: 5,
                                display: 'flex',
                                justifyContent: 'start',
                                alignItems: 'center',
                                flexDirection: 'row',
                                overflow: 'auto',
                                width: '100%'
                            }}
                        >
                            <Box sx={{ gap: 1, alignItems: 'center', display: 'flex', flexDirection: 'row' }}>
                                {filteredPhotos?.map((item: MediaOperation, index: number) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            position: 'relative',
                                            width: imageWidth,
                                            height: imageHeight,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                width: '20px',
                                                height: '20px',
                                                right: 4,
                                                zIndex: 1,
                                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                            }}
                                            onClick={() => removeImage(item)}
                                        >
                                            <CloseIcon
                                                width="14"
                                                height="14"
                                            />
                                        </IconButton>
                                        {'mediaType' in item && item?.mediaType === 'image' ? (
                                            <Image
                                                draggable="false"
                                                src={'draftUrl' in item ? item.draftUrl : 'mediaUrl' in item ? item.mediaUrl ?? '' : ''}
                                                width={imageWidth}
                                                height={imageHeight}
                                                priority
                                                style={{ borderRadius: `${imageRadius}px`, objectFit: 'cover' }}
                                                alt="Image"
                                            />
                                        ) : (
                                            <video
                                                width={imageWidth}
                                                height={imageHeight}
                                                style={{ borderRadius: `${imageRadius}px`, objectFit: 'cover' }}
                                                controls
                                            >
                                                <source
                                                    src={'draftUrl' in item ? item.draftUrl : 'mediaUrl' in item ? item.mediaUrl ?? '' : ''}
                                                    type={'mediaUrl' in item ? fileHelper.getFileNameAndExtension(item.mediaUrl ?? '').extension : 'mp4'}
                                                />
                                                Your browser does not support the video tag.
                                            </video>
                                        )}
                                    </Box>
                                ))}
                                <Box
                                    sx={{
                                        width: imageWidth,
                                        height: imageHeight,
                                        flexShrink: 0,
                                    }}
                                >
                                    <ImagePicker
                                        onChange={handleAvatarChange}
                                        allowMultiple
                                    >
                                        <Button
                                            component="label"
                                            disabled={filteredPhotos?.length >= 10}
                                            sx={{
                                                width: '80px',
                                                padding: 0,
                                                height: '80px',
                                                bgcolor: '#fff',
                                                borderRadius: '10px',
                                                opacity: filteredPhotos?.length >= 10 ? 0.5 : 1,
                                                cursor: filteredPhotos?.length >= 10 ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            <AddImage />
                                        </Button>
                                    </ImagePicker>
                                </Box>
                            </Box>
                        </MUIBoxScrollBehavior>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'grayscale.gray04',
                                mt: 2,
                                fontSize: '12px',
                                fontWeight: 400,
                                lineHeight: '100%',
                            }}
                        >
                            {t('image_upload_help_text', '1200x810px 사이즈의 이미지를 업로드해 주세요.')}
                        </Typography>
                    </>
                ) : (mediaType === 'URL' && initialData?.url && initialData.url.trim() !== '') ? (
                        <Box sx={{ mb: 5 }}>
                            <JoyPlayer
                                uniqueId={`${initialData.url}${moment().millisecond()}`}
                                isResizeWithOutSwiper={true}
                                src={cleanYouTubeUrl(initialData.url)}
                                controls={false}
                            />
                            <Box sx={{ mt: 3 }}>
                                <ButtonHandleUrl
                                    onEditUrl={onBackToUrlScreen}
                                    onDeleteUrlCurrent={() => {
                                        // Create a URL-type media object to pass to handleDeleteUrl
                                        const urlMedia: MediaOperation = {
                                            mediaType: 'url',
                                            mediaUrl: initialData.url,
                                            order: 1,
                                        } as MediaOperation;
                                        handleDeleteUrl?.(urlMedia);
                                        // Switch back to image type after deleting URL
                                        setMediaType('image');
                                        onMediaTypeChange?.('image');
                                    }}
                                />
                            </Box>
                        </Box>
                    ) : null}

                    {/* Caption - Rich Text Editor */}
                    <Box
                        sx={{
                            background: 'background.default',
                            pb: 4,
                            paddingTop: 0,
                        }}
                    >
                        <CaptionEditor
                            value={caption}
                            onChange={(newCaption) => {
                                setCaption(newCaption);
                                onCaptionChange?.(newCaption);
                            }}
                            placeholder={t('content_placeholder', '내용을 입력하세요.')}
                        />
                    </Box>

                    {/* Product name - Only show when market */}
                    {boardType === 'market' && (
                        <Box sx={{ mt: 4 }}>
                            <Typography
                                variant="h2_14Medium"
                                component="div"
                                sx={{ mb: 3, color: 'grayscale.gray07' }}
                            >
                                {t('product_name', '상품명')}
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder={t('product_name_placeholder', '상품명을 입력하세요.')}
                                value={productName}
                                onChange={(e) => {
                                    const newProductName = e.target.value;
                                    setProductName(newProductName);
                                    onDataChange?.({ groupId: selectedGroup, tags, caption, boardType, price, productName: newProductName });
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        background: '#FFF',
                                        '& fieldset': {
                                            border: 'none',
                                            pl: 0
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            border: 'none'
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                        py: 0,
                                        px: 4,
                                        pl: 0
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Box>
                {/* Post tags */}
                <Box
                    sx={{
                        px: 5,
                        pt: 3,
                        pb: boardType === 'market' ? 3.5 : 15,
                        backgroundColor: 'white'
                    }}
                >
                    <Typography
                        variant="h2_14Medium"
                        component="div"
                        sx={{ mb: 3, color: 'grayscale.gray07' }}
                    >
                        {t('tag_input', '태그 입력')}
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder={t('tag_placeholder', '태그를 입력해주세요.')}
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                background: '#FFF',
                                '& fieldset': {
                                    borderColor: 'grayscale.gray02',
                                    borderRadius: '16px',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    border: '1px solid #000',
                                },
                            },
                        }}
                        InputProps={{
                            sx: {
                                borderRadius: '16px',
                                fontSize: '12px',
                                fontWeight: 400,
                                lineHeight: '100%',
                            },
                            endAdornment: (
                                <Button
                                    onClick={handleAddTag}
                                    disabled={!tagInput.trim() || tags.length >= MAX_TAGS}
                                    sx={{ minWidth: '40px', mr: -2 }}
                                >
                                    <Typography
                                        variant="h2_12Medium"
                                        color={!tagInput.trim() || tags.length >= MAX_TAGS ? 'grayscale.gray04' : 'primary.main'}
                                        sx={{ width: '23px' }}
                                        lineHeight={'100%'}
                                    >
                                        {t('tag_input_button', '입력')}
                                    </Typography>
                                </Button>
                            ),
                        }}
                    />
                    {/* List tags */}
                    {tags.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                flexWrap: 'nowrap',
                                mt: 3,
                                pb: 1,
                                scrollbarWidth: 'none', 
                                msOverflowStyle: 'none',
                                '&::-webkit-scrollbar': {
                                    display: 'none',
                                },
                                '& > *': {
                                    flexShrink: 0,
                                },
                            }}
                        >
                            {tags.map((tag, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        backgroundColor: '#fff',
                                        border: '1px solid',
                                        borderColor: 'grayscale.gray03',
                                        borderRadius: '100px',
                                        height: '40px',
                                        px: 3,
                                        gap: 1.5,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            color: 'grayscale.gray07',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {tag}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            const newTags = tags.filter((_, i) => i !== index);
                                            setTags(newTags);
                                            onDataChange?.({ groupId: selectedGroup, tags: newTags, caption, boardType, price, productName });
                                        }}
                                        sx={{
                                            padding: 0,
                                            width: '16px',
                                            height: '16px',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                            },
                                        }}
                                    >
                                        <CloseIcon width="14" height="14" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
                
                {/* Product price */}
                {boardType === 'market' && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            px: 5,
                            pt: 3,
                            pb: 4,
                            backgroundColor: 'white',
                            borderTop: '1px solid',
                            borderColor: 'grayscale.gray02',
                            marginTop: '-6px'
                        }}
                    >
                        <Typography
                            variant="h2_14Medium"
                            component="div"
                            sx={{ width: '100%', color: 'grayscale.gray07' }}
                        >
                            {t('price_input', '가격 입력')}
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder={t('price_placeholder', '금액을 입력해주세요.')}
                            value={formatPrice(price)}
                            onChange={handlePriceChange}
                            sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                    background: '#FFF',
                                    '& fieldset': {
                                        borderColor: 'grayscale.gray02',
                                        borderRadius: '16px',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        border: '1px solid #000',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: '14px',
                                    py: 3.5,
                                    px: 4,
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>
        );
    }
);

NewWritingForm.displayName = 'NewWritingForm';

export default NewWritingForm;
